import React from 'react';
import { Link } from 'react-router-dom';
import {
  List,
  Show,
  Edit,
  SimpleForm,
  TextInput,
  BooleanInput,
  Create,
  SelectInput,
  useListContext,
  useShowContext,
  useGetList,
  useRefresh,
  useDataProvider,
} from 'react-admin';
import { useBreadcrumbContext } from '../components/layout/AppBreadcrumb';
import { ReactAdminPagination } from '../components/ui/react-admin-pagination';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Avatar,
  Rating,
} from '@mui/material';
import { CheckCircle, XCircle, Shield, Star, Calendar, MapPin, Users, Trophy, ExternalLink, Copy, Ban, Pause, RotateCcw } from 'lucide-react';
import { SearchIntegration } from '../components/common/SearchIntegration';
import { logger, LogCategory } from '../utils/logger';
import { validateEmail, validateUrl, validatePhone, validatePasswordStrength, validateDateFormat, ADMIN_ACTION_REASON } from '../utils/validators';
import { CustomDataProvider } from '../types/dataProvider';
import { ListScreen } from '../components/ui/list-screen';
import { StatCard, StatRow } from '../components/ui/stat-card';
import { SegmentedTabs } from '../components/ui/segmented-tabs';
import { DataTable, DataColumn } from '../components/ui/data-table';
import { SortMenu } from '../components/ui/sort-menu';
import { FilterTokens } from '../components/ui/filter-controls';
import { ConfirmDialog, ConfirmTone } from '../components/ui/confirm-dialog';
import { AvatarName, Stars as StarsCell, RoleBadge } from '../components/ui/data-cells';
import { toastSuccess, toastError } from '../lib/toast';
import { cn } from '../lib/utils';

// Enhanced API interfaces for detailed user data
interface PlayerReviewItem {
  id: number;
  rating: number;
  comment: string;
  created_at: string;
  reviewed_user?: {
    name: string;
    email: string;
  };
  reviewer?: {
    name: string;
    email: string;
  };
  match: {
    id: number;
    title: string;
    sport_type: string;
    date_time: string | null;
  };
}

// Clickable User Image Component
const ClickableUserImage = ({
  src,
  alt,
  fallback,
  size = 32,
  showUrl = true,
  className = ""
}: {
  src?: string | null,
  alt: string,
  fallback: string,
  size?: number,
  showUrl?: boolean,
  className?: string
}) => {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.error(LogCategory.UI, 'Failed to copy text', err, 'Users');
    }
  };

  const openInNewTab = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <Avatar
        src={src || undefined}
        onClick={() => setModalOpen(true)}
        sx={{
          width: size,
          height: size,
          cursor: 'pointer',
          transition: 'transform 0.2s',
          '&:hover': {
            transform: 'scale(1.05)',
          },
          boxShadow: src ? '0 2px 8px 0 rgb(32 40 45 / 12%)' : 'none',
        }}
        className={className}
      >
        {fallback}
      </Avatar>

      {/* Simple Modal using absolute positioning */}
      {modalOpen && (
        <div
          className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          style={{ margin: 0, padding: 0 }}
          onClick={() => setModalOpen(false)}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-ink">Profile Picture</h3>
              <button
                onClick={() => setModalOpen(false)}
                className="text-ink-2 hover:text-ink-2"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              {src ? (
                <>
                  <div className="flex justify-center">
                    <img
                      src={src}
                      alt={alt}
                      className="max-w-full max-h-96 object-contain rounded-lg"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>
                  {showUrl && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-ink">Image URL:</label>
                      <div className="flex items-center gap-2 p-2 bg-surface-2 rounded border">
                        <code className="flex-1 text-sm text-ink break-all">{src}</code>
                        <div className="flex gap-1">
                          <button
                            onClick={() => copyToClipboard(src)}
                            className="p-1 rounded border hover:bg-surface-2 flex items-center justify-center"
                            title="Copy URL"
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => openInNewTab(src)}
                            className="p-1 rounded border hover:bg-surface-2 flex items-center justify-center"
                            title="Open in new tab"
                          >
                            <ExternalLink className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                      {copied && (
                        <p className="text-xs text-success">URL copied to clipboard!</p>
                      )}
                      <p className="text-xs text-ink-2">
                        💡 Use the copy button to get the URL for Supabase platform verification
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="w-24 h-24 bg-surface-2 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-ink-3">{fallback}</span>
                  </div>
                  <p className="text-ink-2">No profile picture available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// User status badges (active / verified / trainer)
const UserStatusBadges = ({ record }: { record: any }) => (
  <div className="flex flex-wrap items-center gap-1.5">
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-pill px-2 py-0.5 text-xs font-semibold',
        record.is_active ? 'bg-success-bg text-success' : 'bg-error-bg text-error',
      )}
    >
      {record.is_active ? <CheckCircle className="size-3" /> : <XCircle className="size-3" />}
      {record.is_active ? 'Active' : 'Inactive'}
    </span>
    {record.is_email_verified && (
      <span className="inline-flex items-center gap-1 rounded-pill bg-indigo-tint px-2 py-0.5 text-xs font-semibold text-indigo">
        <Shield className="size-3" /> Verified
      </span>
    )}
  </div>
);

const USER_TABS = [
  { value: 'all', label: 'All' },
  { value: 'trainers', label: 'Trainers' },
  { value: 'regular', label: 'Regular' },
];

const USER_SORT = [
  { field: 'name', label: 'Name' },
  { field: 'created_at', label: 'Joined' },
  { field: 'sessions_created', label: 'Sessions' },
  { field: 'player_average_rating', label: 'Rating' },
  { field: 'id', label: 'ID' },
];
const USER_SORT_LABELS = Object.fromEntries(USER_SORT.map((f) => [f.field, f.label]));

const UserStatusTabs = () => {
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const current = (filterValues.user_type as string) || 'all';
  const onChange = (value: string) => {
    const next = { ...filterValues };
    if (value === 'all') {
      delete next.user_type;
      delete next.is_trainer;
    } else if (value === 'trainers') {
      next.user_type = 'trainers';
      next.is_trainer = true;
    } else {
      next.user_type = 'regular';
      next.is_trainer = false;
    }
    setFilters(next, displayedFilters);
  };
  return <SegmentedTabs tabs={USER_TABS} value={current} onChange={onChange} />;
};

const UserStats = () => {
  const base = { pagination: { page: 1, perPage: 1 }, sort: { field: 'created_at', order: 'DESC' } } as const;
  const all = useGetList('users', { ...base, filter: {} });
  const trainers = useGetList('users', { ...base, filter: { is_trainer: true } });
  const regular = useGetList('users', { ...base, filter: { is_trainer: false } });
  const n = (v?: number) => (v === undefined ? '—' : v);
  return (
    <StatRow>
      <StatCard primary label="Total users" value={n(all.total)} />
      <StatCard label="Trainers" value={n(trainers.total)} />
      <StatCard label="Regular users" value={n(regular.total)} />
      <StatCard label="Showing" value={<ResultCountValue />} />
    </StatRow>
  );
};

const ResultCountValue = () => {
  const { total } = useListContext();
  return <>{total ?? '—'}</>;
};

const UsersListBody = () => {
  const columns: DataColumn<any>[] = [
    { key: 'id', header: 'ID', sortable: true, width: '1px', cell: (r) => <span className="tabular-nums text-ink-2">#{r.id}</span> },
    {
      key: 'name',
      header: 'User',
      sortable: true,
      cell: (r) => (
        <AvatarName src={r.profile_picture_url} name={r.name || 'Unknown'} subtext={r.email} />
      ),
    },
    { key: 'role', header: 'Role', cell: (r) => <RoleBadge role={r.is_trainer ? 'Trainer' : 'User'} /> },
    { key: 'status', header: 'Status', cell: (r) => <UserStatusBadges record={r} /> },
    {
      key: 'player_average_rating',
      header: 'Rating',
      sortable: true,
      cell: (r) => <StarsCell value={r.player_average_rating || r.rating || 0} />,
    },
    {
      key: 'sessions_created',
      header: 'Sessions',
      sortable: true,
      align: 'right',
      cell: (r) => <span className="tabular-nums">{r.sessions_created || 0}</span>,
    },
    {
      key: 'created_at',
      header: 'Joined',
      sortable: true,
      cell: (r) =>
        new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }),
    },
  ];
  return (
    <ListScreen
      stats={<UserStats />}
      tabs={<UserStatusTabs />}
      actions={<SortMenu fields={USER_SORT} />}
      tokens={<FilterTokens fields={[]} sortLabels={USER_SORT_LABELS} />}
    >
      <DataTable columns={columns} rowClick="show" emptyMessage="No users found." />
      <div className="pt-1">
        <ReactAdminPagination />
      </div>
    </ListScreen>
  );
};

// Professional User List Component
export const UserList = (props: any) => (
  <List
    {...props}
    actions={false}
    empty={false}
    pagination={false}
    sort={{ field: 'created_at', order: 'DESC' }}
    perPage={25}
    component="div"
  >
    <SearchIntegration filterKey="q" />
    <UsersListBody />
  </List>
);

// User Activity Tab Component
const UserActivityTab: React.FC<{ userId?: number | string }> = ({ userId }) => {
  const dataProvider = useDataProvider<CustomDataProvider>();
  const [days, setDays] = React.useState(30);
  const [data, setData] = React.useState<any | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    if (userId === undefined || userId === null) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await dataProvider.getUserActivity({ id: userId as any, days });
      setData(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [dataProvider, userId, days]);

  React.useEffect(() => {
    load();
  }, [load]);

  if (!userId) {
    return <p className="text-sm text-ink-2">User id not available.</p>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-ink">Activity</h2>
        <select
          className="text-sm border rounded px-2 py-1 bg-white"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          disabled={loading}
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={180}>Last 180 days</option>
          <option value={365}>Last year</option>
        </select>
      </div>

      {loading && <p className="text-sm text-ink-2">Loading…</p>}
      {error && <p className="text-sm text-error">{error}</p>}

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border border-border bg-card">
            <CardContent className="p-6">
              <div className="text-xs text-ink-2">Sessions created</div>
              <div className="text-2xl font-semibold text-ink mt-1">
                {data.sessions_created ?? 0}
              </div>
            </CardContent>
          </Card>
          <Card className="border border-border bg-card">
            <CardContent className="p-6">
              <div className="text-xs text-ink-2">Reviews given</div>
              <div className="text-2xl font-semibold text-ink mt-1">
                {data.reviews_given ?? 0}
              </div>
            </CardContent>
          </Card>
          <Card className="border border-border bg-card">
            <CardContent className="p-6">
              <div className="text-xs text-ink-2">Feedback submitted</div>
              <div className="text-2xl font-semibold text-ink mt-1">
                {data.feedback_submitted ?? 0}
              </div>
            </CardContent>
          </Card>
          <Card className="border border-border bg-card">
            <CardContent className="p-6">
              <div className="text-xs text-ink-2">Last active</div>
              <div className="text-sm font-medium text-ink mt-1">
                {data.last_active ? new Date(data.last_active).toLocaleString() : '—'}
              </div>
              <div className="text-xs text-ink-2 mt-2">
                Range: {data.date_range_days ?? days} days
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <p className="text-xs text-ink-2">
        Bookings and chat activity are not yet returned by the backend.
      </p>
    </div>
  );
};

// User Show Content Component
const UserShowContent = () => {
  const { record, isLoading } = useShowContext();
  const refresh = useRefresh();
  const dataProvider = useDataProvider<CustomDataProvider>();
  const { setData } = useBreadcrumbContext();

  // Update breadcrumb context when record loads
  React.useEffect(() => {
    if (record) {
      setData({
        resourceId: record.id.toString(),
        resourceName: record.name || `User #${record.id}`,
        resourceType: 'user',
      });
    }
  }, [record, setData]);

  // Shared confirm dialog for destructive account actions
  const [confirm, setConfirm] = React.useState<null | {
    title: string;
    consequence?: string;
    tone: ConfirmTone;
    confirmLabel: string;
    reasonRequired?: boolean;
    reasonMinLength?: number;
    reasonMaxLength?: number;
    run: (reason?: string) => Promise<void>;
  }>(null);

  if (isLoading) {
    return <div className="p-6 text-center">Loading user details...</div>;
  }

  if (!record) {
    return <div className="p-6 text-center">User not found</div>;
  }

  // Handler functions for user account management
  const suspendUser = async (reason?: string) => {
    try {
      await dataProvider.suspendUser({ id: record.id, reason: reason || '', notes: '' });
      toastSuccess('User account suspended');
      refresh();
    } catch {
      toastError('Failed to suspend user account');
    }
  };

  const unsuspendUser = async () => {
    try {
      await dataProvider.unsuspendUser({ id: record.id });
      toastSuccess('User account unsuspended');
      refresh();
    } catch {
      toastError('Failed to unsuspend user account');
    }
  };

  const banUser = async (reason?: string) => {
    try {
      await dataProvider.banUser({ id: record.id, reason: reason || '', notes: '' });
      toastSuccess('User account banned');
      refresh();
    } catch {
      toastError('Failed to ban user account');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Record header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <ClickableUserImage
            src={record?.profile_picture_url}
            alt={`${record?.name} profile picture`}
            fallback={record?.name?.charAt(0).toUpperCase() || 'U'}
            size={60}
          />
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-[24px] font-bold leading-none text-ink" style={{ letterSpacing: '-0.02em' }}>
                {record?.name || 'Unknown User'}
              </h1>
              <RoleBadge role={record?.is_trainer ? 'Trainer' : 'User'} />
            </div>
            <p className="mt-1 text-sm text-ink-2">
              {record?.email}
              {record?.username && ` · @${record.username}`}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {record.is_suspended ? (
            <button
              onClick={unsuspendUser}
              className="inline-flex h-9 items-center gap-2 rounded-pill bg-success px-4 text-sm font-semibold text-white hover:bg-success/90"
            >
              <RotateCcw className="size-4" />
              Unsuspend
            </button>
          ) : (
            !record.is_banned && (
              <button
                onClick={() =>
                  setConfirm({
                    title: 'Suspend user account',
                    consequence: 'The user cannot access the app until unsuspended.',
                    tone: 'warning',
                    confirmLabel: 'Suspend',
                    reasonRequired: true,
                    ...ADMIN_ACTION_REASON,
                    run: suspendUser,
                  })
                }
                className="inline-flex h-9 items-center gap-2 rounded-pill border border-hairline px-4 text-sm font-semibold text-ink hover:bg-surface-2"
              >
                <Pause className="size-4" />
                Suspend
              </button>
            )
          )}

          {!record.is_banned && (
            <button
              onClick={() =>
                setConfirm({
                  title: 'Ban user permanently',
                  consequence: 'The user is permanently banned from the platform. Most severe action.',
                  tone: 'destructive',
                  confirmLabel: 'Ban',
                  reasonRequired: true,
                  ...ADMIN_ACTION_REASON,
                  run: banUser,
                })
              }
              className="inline-flex h-9 items-center gap-2 rounded-pill bg-error px-4 text-sm font-semibold text-white hover:bg-error/90"
            >
              <Ban className="size-4" />
              Ban
            </button>
          )}
        </div>
      </div>

      {/* Suspension/Ban Status Banner */}
      {(record.is_suspended || record.is_banned) && (
        <Card className={`border ${
          record.is_banned
            ? 'border-error bg-error-bg'
            : 'border-warning bg-warning-bg'
        }`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              {record.is_banned ? (
                <Ban className="w-5 h-5 text-error" />
              ) : (
                <Pause className="w-5 h-5 text-warning" />
              )}
              <span className={`font-semibold ${
                record.is_banned ? 'text-error' : 'text-warning'
              }`}>
                User account is {record.is_banned ? 'permanently banned' : 'suspended'}
              </span>
            </div>
            {record.suspension_reason && (
              <div className="mt-2 text-sm text-ink-2">
                <strong className="font-semibold text-ink">Reason:</strong> {record.suspension_reason}
              </div>
            )}
            {record.suspension_until && (
              <div className="mt-1 text-sm text-ink-2">
                <strong className="font-semibold text-ink">Suspended Until:</strong> {new Date(record.suspension_until).toLocaleString()}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Main Tabs Interface */}
      <Tabs defaultValue="general" className="w-full">
        <TabsList>
          <TabsTrigger value="general">
            General Information
          </TabsTrigger>
          <TabsTrigger value="sessions">
            Sessions ({(record?.sessions_created || 0) + (record?.sessions_joined || 0)})
          </TabsTrigger>
          <TabsTrigger value="reviews">
            Reviews ({(record?.player_reviews_given_count || record?.reviews_given_count || 0) + (record?.player_reviews_received_count || record?.player_reviews_count || record?.reviews_received_count || 0)})
          </TabsTrigger>
          <TabsTrigger value="activity">
            Activity
          </TabsTrigger>
        </TabsList>

        {/* General Information Tab */}
        <TabsContent value="general" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Profile Card */}
            <Card className="border border-border bg-card">
              <CardContent className="p-6">
                <div className="flex items-center space-x-4 mb-4">
                  <ClickableUserImage
                    src={record?.profile_picture_url}
                    alt={`${record?.name} profile picture`}
                    fallback={record?.name?.charAt(0).toUpperCase() || 'U'}
                    size={80}
                  />
                  <div>
                    <h3 className="text-xl font-semibold text-ink">{record?.name}</h3>
                    <p className="text-ink">
                      @{record?.username}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div>
                    <label className="text-sm font-medium text-ink">Bio</label>
                    <p className="text-sm text-ink">{record?.bio || 'No bio available'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Details Card */}
            <Card className="border border-border bg-card">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4 text-ink">Contact Details</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-ink">Email</label>
                    <p className="text-sm text-ink">{record?.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-ink">Phone</label>
                    <p className="text-sm text-ink">{record?.phone || 'Not provided'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-ink">Gender</label>
                    <p className="text-sm text-ink">{record?.gender || 'Not specified'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-ink">Birth Date</label>
                    <p className="text-sm text-ink">{record?.birthdate || 'Not provided'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Account Status Card */}
            <Card className="border border-border bg-card">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4 text-ink">Account Status</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-ink">Status</label>
                    <Badge variant={record?.is_active ? "default" : "secondary"}>
                      {record?.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-ink">Email Verified</label>
                    <Badge variant={record?.is_email_verified ? "default" : "outline"}>
                      {record?.is_email_verified ? "Verified" : "Unverified"}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Trainer Information Card - Only shown for trainers */}
            {record?.is_trainer && (
              <Card className="md:col-span-2 border border-indigo/20 bg-indigo-tint">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4 text-ink flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-indigo" />
                    Trainer Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-ink">Trainer Bio</label>
                      <p className="text-sm text-ink">{record?.trainer_bio || 'Not provided'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-ink">Specialties</label>
                      {Array.isArray(record?.trainer_specialties) && record.trainer_specialties.length > 0 ? (
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {record.trainer_specialties.map((sport: any) => (
                            <Badge key={sport?.id ?? sport?.key} variant="outline">
                              {sport?.name_en || sport?.key || 'Unknown'}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-ink">Not specified</p>
                      )}
                    </div>
                    <div>
                      <label className="text-sm font-medium text-ink">Hourly Rate</label>
                      <p className="text-sm text-ink">
                        {record?.trainer_hourly_rate ? `$${record.trainer_hourly_rate}` : 'Not set'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-ink">Availability</label>
                      <p className="text-sm text-ink">{record?.trainer_availability_text || 'Not provided'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Enhanced Statistics Card */}
            <Card className="border border-border bg-card">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4 text-ink">Activity Statistics</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-ink">Average Rating Received</label>
                    <div className="flex items-center space-x-1">
                      <span className="text-sm font-medium text-ink">
                        {record?.player_average_rating ? record.player_average_rating.toFixed(2) :
                          record?.average_rating_received ? record.average_rating_received.toFixed(2) :
                            record?.overall_average_rating ? record.overall_average_rating.toFixed(2) : 'N/A'}
                      </span>
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-ink">Total Activity</label>
                    <span className="text-sm font-medium text-ink">{record?.total_activity || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-ink">Sessions Created</label>
                    <div className="flex items-center space-x-1">
                      <span className="text-sm font-medium text-ink">{record?.sessions_created || 0}</span>
                      <Trophy className="w-4 h-4 text-success" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-ink">Sessions Joined</label>
                    <div className="flex items-center space-x-1">
                      <span className="text-sm font-medium text-ink">{record?.sessions_joined || 0}</span>
                      <Users className="w-4 h-4 text-indigo" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-ink">Player Reviews Given</label>
                    <div className="flex items-center space-x-1">
                      <span className="text-sm font-medium text-ink">{record?.player_reviews_given_count || record?.reviews_given_count || 0}</span>
                      <Star className="w-4 h-4 text-warning" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-ink">Player Reviews Received</label>
                    <div className="flex items-center space-x-1">
                      <span className="text-sm font-medium text-ink">{record?.player_reviews_received_count || record?.player_reviews_count || record?.reviews_received_count || 0}</span>
                      <Star className="w-4 h-4 text-orange-600" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Timestamps Card */}
            <Card className="md:col-span-2 border border-border bg-card">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4 text-ink">Account Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-ink">Created At</label>
                    <p className="text-sm text-ink">
                      {record?.created_at ? new Date(record.created_at).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-ink">Last Updated</label>
                    <p className="text-sm text-ink">
                      {record?.updated_at ? new Date(record.updated_at).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Sessions Tab */}
        <TabsContent value="sessions" className="space-y-6">
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-ink">Session Activity</h2>

            <Tabs defaultValue="created" className="w-full">
              <TabsList>
                <TabsTrigger value="created">
                  Created ({record?.sessions_created || 0})
                </TabsTrigger>
                <TabsTrigger value="joined">
                  Joined ({record?.sessions_joined || 0})
                </TabsTrigger>
              </TabsList>

              {/* Sessions Created Tab */}
              <TabsContent value="created" className="space-y-4">
                <Card className="border border-border bg-card">
                  <CardContent className="p-0">
                    <div className="p-6 border-b">
                      <h3 className="text-lg font-semibold text-ink flex items-center gap-2">
                        <Trophy className="w-5 h-5" />
                        Sessions Created
                      </h3>
                      <p className="text-sm text-ink">All sessions organized by this user</p>
                    </div>
                    <div className="p-6">
                      {record?.sessions_created_history?.length > 0 ? (
                        <div className="space-y-3">
                          {record.sessions_created_history.map((session: any) => (
                            <Link
                              key={session.id}
                              to={`/sessions/${session.id}/show`}
                              className="block p-4 bg-surface-2 rounded-lg border hover:bg-surface-2 transition-colors"
                            >
                              <div className="flex items-center gap-4">
                                {session.location?.image_url && (
                                  <img
                                    src={session.location.image_url}
                                    alt={session.title}
                                    className="w-16 h-16 object-cover rounded-lg"
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement;
                                      target.style.display = 'none';
                                    }}
                                  />
                                )}
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="font-medium text-ink">{session.title}</span>
                                    <Badge variant="outline">{session.sport_type}</Badge>
                                    <Badge variant={session.status === 'completed' ? 'default' : session.status === 'cancelled' ? 'destructive' : 'secondary'}>
                                      {session.status}
                                    </Badge>
                                  </div>
                                <div className="text-sm text-ink space-y-1">
                                  {session.start_time && (
                                    <div className="flex items-center gap-1">
                                      <Calendar className="w-4 h-4" />
                                      {new Date(session.start_time).toLocaleDateString()} at {new Date(session.start_time).toLocaleTimeString()}
                                    </div>
                                  )}
                                  {session.location && (
                                    <div className="flex items-center gap-1">
                                      <MapPin className="w-4 h-4" />
                                      {session.location.name}
                                    </div>
                                  )}
                                  <div className="flex items-center gap-1">
                                    <Users className="w-4 h-4" />
                                    {session.current_participants || 0}/{session.max_participants} participants • {session.duration_minutes} minutes
                                  </div>
                                </div>
                                </div>
                                <div className="text-xs text-ink text-right">
                                  <div>Session ID: #{session.id}</div>
                                  <div>Created: {new Date(session.created_at).toLocaleDateString()}</div>
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center text-ink py-8">
                          {record?.sessions_created > 0 ? (
                            <div className="space-y-2">
                              <p>This user has created <strong>{record?.sessions_created}</strong> sessions</p>
                              <p className="text-sm text-ink-2">
                                📋 Backend Enhancement Needed: The detailed session history is not yet available.
                                <br />
                                Please update the API to include "sessions_created_history" array.
                              </p>
                            </div>
                          ) : (
                            "No sessions created yet"
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Sessions Joined Tab */}
              <TabsContent value="joined" className="space-y-4">
                <Card className="border border-border bg-card">
                  <CardContent className="p-0">
                    <div className="p-6 border-b">
                      <h3 className="text-lg font-semibold text-ink flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        Sessions Joined
                      </h3>
                      <p className="text-sm text-ink">All sessions this user participated in</p>
                    </div>
                    <div className="p-6">
                      {record?.sessions_joined_history?.length > 0 ? (
                        <div className="space-y-3">
                          {record.sessions_joined_history.map((session: any) => (
                            <Link
                              key={session.id}
                              to={`/sessions/${session.id}/show`}
                              className="block p-4 bg-surface-2 rounded-lg border hover:bg-surface-2 transition-colors"
                            >
                              <div className="flex items-center gap-4">
                                {session.location?.image_url && (
                                  <img
                                    src={session.location.image_url}
                                    alt={session.title}
                                    className="w-16 h-16 object-cover rounded-lg"
                                    onError={(e) => {
                                      const target = e.target as HTMLImageElement;
                                      target.style.display = 'none';
                                    }}
                                  />
                                )}
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className="font-medium text-ink">{session.title}</span>
                                    <Badge variant="outline">{session.sport_type}</Badge>
                                    <Badge variant={session.status === 'completed' ? 'default' : session.status === 'cancelled' ? 'destructive' : 'secondary'}>
                                      {session.status}
                                    </Badge>
                                  </div>
                                <div className="text-sm text-ink space-y-1">
                                  {session.start_time && (
                                    <div className="flex items-center gap-1">
                                      <Calendar className="w-4 h-4" />
                                      {new Date(session.start_time).toLocaleDateString()} at {new Date(session.start_time).toLocaleTimeString()}
                                    </div>
                                  )}
                                  {session.location && (
                                    <div className="flex items-center gap-1">
                                      <MapPin className="w-4 h-4" />
                                      {session.location.name}
                                    </div>
                                  )}
                                  <div className="text-sm">
                                    <div>Booking Status: <span className="font-medium">{session.booking_status || 'Confirmed'}</span></div>
                                  </div>
                                </div>
                                </div>
                                <div className="text-xs text-ink text-right">
                                  <div>Session ID: #{session.id}</div>
                                  <div>Joined: {new Date(session.joined_at || session.created_at).toLocaleDateString()}</div>
                                </div>
                              </div>
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center text-ink py-8">
                          {record?.sessions_joined > 0 ? (
                            <div className="space-y-2">
                              <p>This user has joined <strong>{record.sessions_joined}</strong> sessions</p>
                              <p className="text-sm text-ink-2">
                                📋 Backend Enhancement Needed: The detailed session history is not yet available.
                                <br />
                                Please update the API to include "sessions_joined_history" array.
                              </p>
                            </div>
                          ) : (
                            "No sessions joined yet"
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </TabsContent>

        {/* Reviews Tab */}
        <TabsContent value="reviews" className="space-y-6">
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-ink">Review Activity</h2>

            <Tabs defaultValue="player-reviews-given" className="w-full">
              <TabsList>
                <TabsTrigger value="player-reviews-given">
                  Reviews Given ({record?.player_reviews_given_count || record?.reviews_given_count || 0})
                </TabsTrigger>
                <TabsTrigger value="player-reviews-received">
                  Reviews Received ({record?.player_reviews_received_count || record?.player_reviews_count || record?.reviews_received_count || 0})
                </TabsTrigger>
              </TabsList>

              {/* Player Reviews Given Tab */}
              <TabsContent value="player-reviews-given" className="space-y-4">
                <Card className="border border-border bg-card">
                  <CardContent className="p-0">
                    <div className="p-6 border-b">
                      <h3 className="text-lg font-semibold text-ink flex items-center gap-2">
                        <Star className="w-5 h-5" />
                        Player Reviews Given
                      </h3>
                      <p className="text-sm text-ink">Reviews given to other players</p>
                    </div>
                    <div className="p-6">
                      {record?.player_reviews_given?.length > 0 ? (
                        <div className="space-y-3">
                          {record.player_reviews_given.map((review: PlayerReviewItem) => (
                            <div key={review.id} className="p-4 bg-surface-2 rounded-lg border">
                              <div className="flex items-center gap-2 mb-3">
                                <span className="font-medium text-ink">For: {review.reviewed_user?.name}</span>
                                <span className="text-sm text-ink">({review.reviewed_user?.email})</span>
                                <div className="flex items-center gap-1">
                                  <Rating value={review.rating} readOnly size="small" />
                                  <span className="text-sm font-medium text-ink">({review.rating}/5)</span>
                                </div>
                              </div>
                              <p className="text-sm text-ink mb-3 p-3 bg-white rounded border">"{review.comment}"</p>
                              <div className="text-xs text-ink flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                  <div>Review ID: #{review.id}</div>
                                  <div>Match: {review.match.title} ({review.match.sport_type})</div>
                                </div>
                                <div>Date: {new Date(review.created_at).toLocaleDateString()}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center text-ink py-8">
                          {(record?.player_reviews_given_count || record?.reviews_given_count) > 0 ? (
                            <div className="space-y-2">
                              <p>This user has given <strong>{record?.player_reviews_given_count || record?.reviews_given_count}</strong> player reviews</p>
                              <p className="text-sm text-ink-2">
                                📋 Backend Enhancement Needed: The detailed review history is not yet available.
                                <br />
                                Please update the API to include "player_reviews_given" array.
                              </p>
                            </div>
                          ) : (
                            "No player reviews given yet"
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Player Reviews Received Tab */}
              <TabsContent value="player-reviews-received" className="space-y-4">
                <Card className="border border-border bg-card">
                  <CardContent className="p-0">
                    <div className="p-6 border-b">
                      <h3 className="text-lg font-semibold text-ink flex items-center gap-2">
                        <Star className="w-5 h-5" />
                        Player Reviews Received
                      </h3>
                      <p className="text-sm text-ink">Reviews received from other players</p>
                    </div>
                    <div className="p-6">
                      {record?.player_reviews_received?.length > 0 ? (
                        <div className="space-y-3">
                          {record.player_reviews_received.map((review: PlayerReviewItem) => (
                            <div key={review.id} className="p-4 bg-surface-2 rounded-lg border">
                              <div className="flex items-center gap-2 mb-3">
                                <span className="font-medium text-ink">From: {review.reviewer?.name}</span>
                                <span className="text-sm text-ink">({review.reviewer?.email})</span>
                                <div className="flex items-center gap-1">
                                  <Rating value={review.rating} readOnly size="small" />
                                  <span className="text-sm font-medium text-ink">({review.rating}/5)</span>
                                </div>
                              </div>
                              <p className="text-sm text-ink mb-3 p-3 bg-white rounded border">"{review.comment}"</p>
                              <div className="text-xs text-ink flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                  <div>Review ID: #{review.id}</div>
                                  <div>Match: {review.match.title} ({review.match.sport_type})</div>
                                </div>
                                <div>Date: {new Date(review.created_at).toLocaleDateString()}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center text-ink py-8">
                          {(record?.player_reviews_received_count || record?.player_reviews_count || record?.reviews_received_count) > 0 ? (
                            <div className="space-y-2">
                              <p>This user has received <strong>{record?.player_reviews_received_count || record?.player_reviews_count || record?.reviews_received_count}</strong> player reviews</p>
                              <p className="text-sm text-ink-2">
                                📋 Backend Enhancement Needed: The detailed review history is not yet available.
                                <br />
                                Please update the API to include "player_reviews_received" array.
                              </p>
                            </div>
                          ) : (
                            "No player reviews received yet"
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="space-y-6">
          <UserActivityTab userId={record?.id} />
        </TabsContent>
      </Tabs>

      {/* Shared confirm dialog (suspend / ban) */}
      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm?.title || ''}
        consequence={confirm?.consequence}
        tone={confirm?.tone || 'destructive'}
        confirmLabel={confirm?.confirmLabel || 'Confirm'}
        reasonRequired={confirm?.reasonRequired}
        reasonMinLength={confirm?.reasonMinLength}
        reasonMaxLength={confirm?.reasonMaxLength}
        onConfirm={async (reason) => {
          if (confirm) await confirm.run(reason);
        }}
      />
    </div>
  );
};

// User Show Component
export const UserShow = (props: any) => (
  <Show {...props} actions={false} title=" " component="div">
    <UserShowContent />
  </Show>
);

// User Edit Component
export const UserEdit = (props: any) => (
  <Edit {...props}>
    <SimpleForm>
      <TextInput source="name" required />
      <TextInput source="username" />
      <TextInput source="email" type="email" required validate={validateEmail} />
      <TextInput source="lastname" />
      <SelectInput
        source="gender"
        choices={[
          { id: 'male', name: 'Male' },
          { id: 'female', name: 'Female' },
          { id: 'other', name: 'Other' },
        ]}
      />
      <TextInput source="birthdate" helperText="Format: dd/mm/yyyy" validate={validateDateFormat('dd/mm/yyyy')} />
      <TextInput source="phone" validate={validatePhone} />
      <TextInput source="profile_picture_url" label="Profile Picture URL" validate={validateUrl} />
      <TextInput source="bio" multiline rows={3} />
      <BooleanInput source="is_active" />
      <BooleanInput source="is_email_verified" />
    </SimpleForm>
  </Edit>
);

// User Create Component
export const UserCreate = (props: any) => (
  <Create {...props}>
    <SimpleForm>
      <TextInput source="name" required />
      <TextInput source="username" />
      <TextInput source="email" type="email" required validate={validateEmail} />
      <TextInput source="password" type="password" required validate={validatePasswordStrength} />
      <TextInput source="lastname" />
      <SelectInput
        source="gender"
        choices={[
          { id: 'male', name: 'Male' },
          { id: 'female', name: 'Female' },
          { id: 'other', name: 'Other' },
        ]}
      />
      <TextInput source="birthdate" helperText="Format: dd/mm/yyyy" validate={validateDateFormat('dd/mm/yyyy')} />
      <TextInput source="phone" validate={validatePhone} />
      <TextInput source="profile_picture_url" label="Profile Picture URL" validate={validateUrl} />
      <TextInput source="bio" multiline rows={3} />
      <BooleanInput source="is_active" defaultValue={true} />
      <BooleanInput source="is_email_verified" defaultValue={false} />
    </SimpleForm>
  </Create>
);